import {Component, EventEmitter, Output} from '@angular/core';
import {AQH_HOR_1, AQH_HOR_4} from '../../../../../../@shared/models/input.module';
import {Horses} from '../../../../../../@shared/services/horses';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-add-horse',
  standalone: false,
  templateUrl: './add-horse.html',
  styleUrl: './add-horse.css'
})
export class AddHorse {
  horseRegistries: { label: string; value: number }[] = [];
  horseGenders: { label: string; value: number }[] = [];

  name = '';
  date_of_birth = '';            // input type="date" gives 'YYYY-MM-DD'
  passport_id = '';
  breed = '';
  color = '';
  vaccination_date = '';
  next_vaccination_date = '';

  selectedRegistry: any = null;
  selectedGender: any = null;

  horseImageFile: File | null = null;
  pedigreeFile: File | null = null;
  vaccinationCardFile: File | null = null;

  // Image preview URLs
  horseImagePreview: string | null = null;
  pedigreePreview: string | null = null;
  vaccinationCardPreview: string | null = null;

  // Validation state
  submitted = false;
  saving = false;

  constructor(private horseService: Horses,
              private modal: NgbActiveModal,
              private toastService: ToastService,
              private translateService: TranslateService) {
  }

  ngOnInit() {
    this.initDropdowns();
    this.translateService.onLangChange.subscribe(() => {
      this.initDropdowns();
    });
  }

  private initDropdowns() {
    this.horseRegistries = [
      { label: 'AQHA', value: 0 },
      { label: 'APHA', value: 1 },
      { label: 'ApHC', value: 2 },
      { label: this.translateService.instant('horses.registry.other'), value: 3 },
    ];
    this.horseGenders = [
      { label: this.translateService.instant('horses.gender.gelding'), value: 0 },
      { label: this.translateService.instant('horses.gender.stallion'), value: 1 },
      { label: this.translateService.instant('horses.gender.mare'), value: 2 },
    ];
  }

  // Validation helpers
  get isNameInvalid(): boolean {
    return this.submitted && !this.name.trim();
  }

  get isGenderInvalid(): boolean {
    return this.submitted && (this.selectedGender === null || this.selectedGender === undefined);
  }

  get isDateOfBirthInvalid(): boolean {
    return this.submitted && !this.date_of_birth;
  }

  get isPassportIdInvalid(): boolean {
    return this.submitted && !this.passport_id.trim();
  }

  get isFormValid(): boolean {
    return !!(
      this.name.trim() &&
      this.selectedGender !== null &&
      this.selectedGender !== undefined &&
      this.date_of_birth &&
      this.passport_id.trim()
    );
  }

  private toISO(date: string): string {
    if (!date) return '';
    // Handle full ISO strings (2025-10-12T00:00:00.000Z)
    if (date.includes('T')) {
      return date.split('T')[0]; // → "2025-10-12"
    }
    // Already YYYY-MM-DD (from input type="date")
    return date;
  }

  onSave() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translateService.instant('horses.form.validationError'));
      return;
    }

    this.saving = true;

    const payload: AQH_HOR_1 = {
      name: this.name.trim(),
      date_of_birth: this.toISO(this.date_of_birth),
      passport_id: this.passport_id.trim(),
      horse_registry_id: this.selectedRegistry?.value ?? this.selectedRegistry ?? 0,
      gender: this.selectedGender,
      breed: this.breed.trim(),
      color: this.color.trim(),
      vaccination_date: this.toISO(this.vaccination_date),
      next_vaccination_date: this.toISO(this.next_vaccination_date),
      owner_name: "",
      owner_phone_number: "",
      image_url: '',
      pedigree_image_url: '',
      vaccination_card_url: '',
    };

    this.horseService.postHorse(payload).subscribe({
      next: (res) => {
        console.log('Horse added:', res);
        let horse = res;

        // Upload images sequentially
        const uploadImages = async () => {
          if (this.horseImageFile) {
            try {
              const imgPayload: AQH_HOR_4 = {
                horse_id: res.horse_id,
                image_type: 'horse_image',
                image: this.horseImageFile
              };
              horse = await this.horseService.uploadHorseImage(imgPayload).toPromise() || horse;
            } catch (e) {
              console.error('Horse image upload failed:', e);
            }
          }
          if (this.pedigreeFile) {
            try {
              const pedigreePayload: AQH_HOR_4 = {
                horse_id: res.horse_id,
                image_type: 'pedigree_image',
                image: this.pedigreeFile
              };
              horse = await this.horseService.uploadHorseImage(pedigreePayload).toPromise() || horse;
            } catch (e) {
              console.error('Pedigree image upload failed:', e);
            }
          }
          if (this.vaccinationCardFile) {
            try {
              const vaccPayload: AQH_HOR_4 = {
                horse_id: res.horse_id,
                image_type: 'vaccination_image',
                image: this.vaccinationCardFile
              };
              horse = await this.horseService.uploadHorseImage(vaccPayload).toPromise() || horse;
            } catch (e) {
              console.error('Vaccination image upload failed:', e);
            }
          }

          this.saving = false;
          this.toastService.success(this.translateService.instant('horses.form.addSuccess'));
          this.modal.close(true);
        };

        uploadImages();
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translateService.instant('horses.form.addError'));
      }
    });
  }

  close() {
    this.modal.close();
  }

  onHorseImageSelected(files: File[]) {
    this.horseImageFile = files[0] || null;
    this.horseImagePreview = this.horseImageFile ? URL.createObjectURL(this.horseImageFile) : null;
  }

  onPedigreeSelected(files: File[]) {
    this.pedigreeFile = files[0] || null;
    this.pedigreePreview = this.pedigreeFile ? URL.createObjectURL(this.pedigreeFile) : null;
  }

  onVaccinationCardSelected(files: File[]) {
    this.vaccinationCardFile = files[0] || null;
    this.vaccinationCardPreview = this.vaccinationCardFile ? URL.createObjectURL(this.vaccinationCardFile) : null;
  }

  removeHorseImage() {
    this.horseImageFile = null;
    this.horseImagePreview = null;
  }

  removePedigree() {
    this.pedigreeFile = null;
    this.pedigreePreview = null;
  }

  removeVaccinationCard() {
    this.vaccinationCardFile = null;
    this.vaccinationCardPreview = null;
  }
}
